import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  OutlinedInput,
  Chip,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";

import ProjectedRevenueCard from "./TabComponents/cards/ProjectedRevenueCard";
import { selectTabData } from "../../../../features/dynamicSlice";
import {
  fetchBranches,
  getProjectedRevenue,
  getLatestAdmissionOrders,
  getTopCourses,
  getAdmissionsList,
} from "../../../../utils/Apis";
import LatestAdmissionOrdersCard from "./TabComponents/cards/LatestAdmissionOrdersCard";
import TopCoursesCard from "./TabComponents/cards/TopCoursesCard";
import DynamicTable from "./TabComponents/DynamicTable";
import BranchWiseChart from "./TabComponents/cards/BranchWiseChart";
import TotalDueFeeCard from "./TabComponents/cards/TotalDueFeeCard";
import StudentCoursesCard from "./TabComponents/cards/StudentCoursesCard";

const columns = [
  { label: "First Name", key: "firstName", sortable: true },
  {
    label: "Created At",
    key: "createdAt",
    sortable: true,
    render: (row) => {
      const date = new Date(row.createdAt);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
  },
  {
    label: "Branch",
    key: "branchId.name",
    render: (row) => row.branchId?.name || "N/A",
    sortable: true,
  },
  // {
  //   label: "Course",
  //   key: "courses[0].name",
  //   render: (row) => (
  //     <div style={{ display: "flex", overflowX: "auto", padding: "10px 0" }}>
  //       {row.courseId?.map((course) => (
  //         <Chip
  //           key={course.courseId}
  //           label={course.name || "N/A"}
  //           style={{ marginRight: "10px" }}
  //         />
  //       ))}
  //     </div>
  //   ),
  //   sortable: true,
  // },
  // {
  //   label: "Status",
  //   key: "userStatus",
  //   sortable: true,
  // },
];

const DashboardContent = () => {
  const dispatch = useDispatch();
  const [admissions, setAdmissions] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(false);
  const newUserData = useSelector((state) => state.user.data);
  const userRole = newUserData?.role;
  const isStudent = userRole === "student";
  const isAdmin = userRole === "admin"; // optional

  const branches = useSelector((state) => state.data.branches?.branches || []);

  const [selectedBranches, setSelectedBranches] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchedCache = useRef({}); // cache of `${branchId}_${start}_${end}`

  // Decode userBranchId from token
  const userBranchId = (() => {
    try {
      return newUserData?.branchId || null;
    } catch (e) {
      return null;
    }
  })();

  // Load branches if not loaded
  useEffect(() => {
    if (!branches.length) {
      dispatch(fetchBranches());
    }
  }, [dispatch, branches.length]);

  // Set default filters on first load
  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const first = new Date(y, m, 1).toLocaleDateString("en-CA");
    const last = new Date(y, m + 1, 0).toLocaleDateString("en-CA");

    setStartDate(first);
    setEndDate(last);

    if (userBranchId) {
      setSelectedBranches([userBranchId]);
    }
  }, [userBranchId]);

  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);
  const handleBranchChange = (e) => {
    const value = e.target.value;

    setSelectedBranches((prev) => {
      // Case 1: If 'All Branches' is selected
      if (value.includes("All")) {
        // If 'All Branches' is selected, include all branches
        return branches.map((b) => b._id);
      }

      // Case 2: If 'All Branches' is selected while some branches are already chosen,
      // we remove "All" and include only those selected branches
      if (prev.includes("All")) {
        return value; // Simply update to the new selection.
      }

      // Case 3: If 'All Branches' is NOT selected, we handle the regular addition/removal
      return value;
    });
  };

  // API Call Logic
  useEffect(() => {
    if (!startDate || !endDate || selectedBranches.length === 0) return;
    const branchIds = selectedBranches.includes("All")
      ? branches.map((b) => b._id)
      : selectedBranches;

    branchIds.forEach((branchId) => {
      const key = `${branchId}_${startDate}_${endDate}`;
      if (!fetchedCache.current[key]) {
        dispatch(
          getProjectedRevenue({
            tab: "projectedRevenue",
            filters: { branchIds, startDate, endDate },
          })
        );
        dispatch(
          getLatestAdmissionOrders({
            tab: "latestAdmissionOrders",
            filters: { branchIds, startDate, endDate },
          })
        );
        dispatch(
          getTopCourses({
            tab: "topCourses",
            filters: { branchIds, startDate, endDate },
          })
        );
        fetchedCache.current[key] = true;
      }
    });
  }, [dispatch, selectedBranches, startDate, endDate, branches]);

  const filteredBranchIds = selectedBranches.includes("All")
    ? branches.map((b) => b._id)
    : selectedBranches;

  useEffect(() => {
    const fetchAdmissions = async () => {
      if (!startDate || !endDate || filteredBranchIds.length === 0) return;

      setLoading(true);
      const filters = {
        branchIds: filteredBranchIds,
        page,
        perPage,
        sortBy: sortField,
        sortOrder,
      };

      const resultAction = await dispatch(
        getAdmissionsList({ tab: "latestAdmissionList", filters })
      );
      const payload = resultAction?.payload;

      if (payload) {
        setAdmissions(payload.data || []);
        setTotal(payload.total || 0);
      }
      setLoading(false);
    };

    fetchAdmissions();
  }, [
    dispatch,
    startDate,
    endDate,
    filteredBranchIds,
    page,
    perPage,
    sortField,
    sortOrder,
  ]);

  return (
    <Box
      sx={{
        pt: 2,
        display: "flex",
        flexDirection: "column",
        // alignItems: "center",
        // textAlign: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <h1 className="text-4xl font-bold">
          Hey!👋 - {newUserData?.firstName}
        </h1>
        {!isStudent && (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}>
            {/* Branch Filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Branches</InputLabel>
              <Select
                multiple
                value={selectedBranches}
                onChange={handleBranchChange}
                input={<OutlinedInput label="Branches" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.includes("All") ? (
                      <Chip key="All" label="All Branches" />
                    ) : (
                      selected.map((id) => {
                        const branch = branches.find((b) => b._id === id);
                        return <Chip key={id} label={branch?.name || id} />;
                      })
                    )}
                  </Box>
                )}
              >
                <MenuItem value="All">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch._id} value={branch._id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Filters */}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
            </Box>
          </Box>
        )}
      </Box>
      {/* Cards */}
      {!isStudent && (
        <Box
          mt={3}
          display="flex"
          gap={3}
          flexWrap="wrap"
          justifyContent="space-evenly"
        >
          <ProjectedRevenueCard
            branchIds={filteredBranchIds}
            startDate={startDate}
            endDate={endDate}
          />
          <LatestAdmissionOrdersCard
            branchIds={filteredBranchIds}
            startDate={startDate}
            endDate={endDate}
          />
          <TopCoursesCard branchIds={filteredBranchIds} />
        </Box>
      )}
      {isStudent && (
        <Box
          mt={3}
          display="flex"
          gap={3}
          flexWrap="wrap"
          justifyContent="space-evenly"
        >
          <TotalDueFeeCard />
          <StudentCoursesCard />
        </Box>
      )}
      {!isStudent && (
        <Box>
          <Box
            mt={5}
            sx={{
              display: "flex",
              flexWrap: "wrap", // Makes it responsive
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            {/* New Component Section (Chart/Other Component) */}
            <Box
              sx={{
                flex: "1 1 48%", // 48% width for the new component
                minWidth: 300, // To avoid collapsing
                maxWidth: "100%", // Full width when on large screens
              }}
            >
              {/* Example: Replace this with your actual chart or other component */}
              <Box
                sx={{
                  // backgroundColor: "#f4f6f8",
                  px: 4,
                  borderRadius: 2,
                  height: "100%", // Make it stretch if needed
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Your Trends
                </Typography>
                {/* Add your Chart component or any other here */}
                <div>
                  <BranchWiseChart
                    branchIds={filteredBranchIds}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </div>
              </Box>
            </Box>

            {/* Table Section */}
            <Box
              sx={{
                flex: "1 1 48%", // 48% width for the table
                minWidth: 300, // To avoid collapsing
                maxWidth: "100%", // Full width when on large screens
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Recent Admissions Activity
              </Typography>
              <DynamicTable
                columns={columns}
                rows={admissions}
                total={total}
                page={page}
                perPage={perPage}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
                onSort={(field) => {
                  const order =
                    sortField === field && sortOrder === "asc" ? "desc" : "asc";
                  setSortField(field);
                  setSortOrder(order);
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                processMode={false}
                onRowClick={(row) => {}}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DashboardContent;
